import { getDuplicationSourceTenantsList } from '@/actions/tenants';
import { Button, LoadingOverlay, SelectInput } from '@payloadcms/ui';
import { useEffect, useState } from 'react';

import { WizardStepHeader } from './WizardStepHeader';

import type { TenantListItem } from '@/actions/tenants';

export const CREATION_MODE = {
  EMPTY: 'empty',
  TEMPLATE: 'template',
} as const;

export type CreationMode = (typeof CREATION_MODE)[keyof typeof CREATION_MODE];

const BTN_CLASS = 'fv-create-new-landing-modal__creation-type-btn';
const SELECTED_CLASS = 'selected';

interface LandingSourceStepProps {
  creationMode: CreationMode | null;
  selectedTenant: TenantListItem | null;
  onModeSelect: (mode: CreationMode) => void;
  onTemplateSelect: (tenant: TenantListItem) => void;
  onBack: () => void;
  onContinue: () => void;
}

export const LandingSourceStep = ({
  creationMode,
  selectedTenant,
  onModeSelect,
  onTemplateSelect,
  onBack,
  onContinue,
}: LandingSourceStepProps) => {
  const [tenants, setTenants] = useState<TenantListItem[] | null>(null);
  const [loadError, setLoadError] = useState('');

  const isTemplateMode = creationMode === CREATION_MODE.TEMPLATE;
  const creationBtnClass = (mode: CreationMode) => (creationMode === mode ? `${BTN_CLASS} ${SELECTED_CLASS}` : BTN_CLASS);
  const loading = isTemplateMode && tenants === null && !loadError;

  useEffect(() => {
    if (!isTemplateMode || tenants !== null || loadError) return;

    let cancelled = false;

    getDuplicationSourceTenantsList()
      .then((list) => {
        if (!cancelled) setTenants(list);
      })
      .catch(() => {
        if (!cancelled) setLoadError('Failed to load landings. Please try again.');
      });

    return () => {
      cancelled = true;
    };
  }, [isTemplateMode, tenants, loadError]);

  const handleModeSelect = (mode: CreationMode) => {
    if (mode === CREATION_MODE.TEMPLATE) setLoadError('');
    onModeSelect(mode);
    if (mode === CREATION_MODE.EMPTY) onContinue();
  };

  const tenantList = tenants ?? [];

  return (
    <div data-testid="create-new-landing-step-source">
      {loading && <LoadingOverlay />}
      <WizardStepHeader title="How do you want to create the landing?" onBack={onBack} backDisabled={loading} />
      <p>Choose whether to start from an empty landing or copy content from an existing one.</p>
      <div className="fv-create-new-landing-modal__creation-type-options">
        <Button
          type="button"
          buttonStyle="secondary"
          icon="edit"
          onClick={() => handleModeSelect(CREATION_MODE.EMPTY)}
          className={creationBtnClass(CREATION_MODE.EMPTY)}
          extraButtonProps={{ 'data-testid': 'create-new-landing-creation-type-empty' }}
        >
          From scratch (empty)
        </Button>
        <Button
          type="button"
          buttonStyle="secondary"
          icon="plus"
          onClick={() => handleModeSelect(CREATION_MODE.TEMPLATE)}
          className={creationBtnClass(CREATION_MODE.TEMPLATE)}
          extraButtonProps={{ 'data-testid': 'create-new-landing-creation-type-template' }}
        >
          From template
        </Button>
      </div>
      {isTemplateMode && (
        <>
          {loadError && <p className="fv-create-new-landing-modal__error">{loadError}</p>}
          {!loadError && !loading && tenantList.length === 0 && (
            <p className="fv-create-new-landing-modal__template-list-empty">
              No template landings available. Only landings whose internal name starts with landing-template- are listed here.
            </p>
          )}
          {!loadError && tenantList.length > 0 && (
            <div className="fv-create-new-landing-modal__template-list" data-testid="create-new-landing-template-select">
              <SelectInput
                name="templateTenant"
                path="templateTenant"
                label="Select a template"
                options={tenantList.map((t) => ({ label: t.displayName, value: t.id }))}
                value={selectedTenant?.id ?? undefined}
                onChange={(option) => {
                  if (!option || Array.isArray(option)) return;
                  const tenant = tenantList.find((t) => t.id === option.value);
                  if (tenant) onTemplateSelect(tenant);
                }}
                isClearable={false}
                required
              />
            </div>
          )}
          <div className="fv-create-new-landing-modal__step-actions">
            <Button
              type="button"
              buttonStyle="primary"
              onClick={onContinue}
              disabled={!selectedTenant || loading || !!loadError}
              extraButtonProps={{ 'data-testid': 'create-new-landing-template-continue' }}
            >
              Continue
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
